/**
 * BUG-1542 — the "adjust a lot's close by N minutes" baseline, as an
 * IsomorphicService (ADR 010 — Isomorphic Services).
 *
 * A lot's close is adjusted from the LATER of its own close and the server's
 * clock. That clamp is BL-1260 AC 8: a lot (or a whole extension group) that has
 * ticked past its close must move FORWARD by the amount the operator asked for,
 * not be pinned to a moment that has already gone. Extending an expired group by
 * one minute must not land it one minute in the past.
 *
 * **Why this is shared rather than a private helper.** It used to be a private
 * `adjustBaseline` inside `ChangeCloseTimeService`, so the server applied the
 * clamp and the modal previewing the change did not — the dialog promised a
 * close time the server would never write. Measured on a lot 11 minutes past its
 * close: the preview read 3:41 PM, the row came back 3:52:31 PM. One rule, two
 * implementations, and only one of them existed.
 *
 * Pure: no I/O, no clock read of its own. **Both callers pass their own "now"**,
 * which is the point — the server passes `Date.now()`, the client passes the
 * server-anchored clock (ADR 024 — Time Model Decision 5), and neither reads a
 * device clock this module could hide.
 */

/**
 * The instant an adjustment is measured from: the lot's own close, or now if
 * that close has already passed.
 *
 * @param closeMs - the lot's current close, epoch ms.
 * @param serverNowMs - the server clock, epoch ms.
 * @returns the later of the two, epoch ms.
 * @sideEffect none — pure.
 */
export function resolveAdjustBaselineMs(closeMs: number, serverNowMs: number): number {
  return Math.max(closeMs, serverNowMs);
}

/**
 * The close instant an adjustment of `signedMs` produces — the baseline above
 * plus the signed shift. Negative shifts (the UI's "Reduce") are legal input and
 * may legitimately resolve to a past instant; refusing that is the server's job,
 * not this function's.
 *
 * @param opts.closeMs - the lot's current close, epoch ms.
 * @param opts.serverNowMs - the server clock, epoch ms.
 * @param opts.signedMs - the shift, positive to extend and negative to reduce.
 * @returns the resulting close instant, epoch ms.
 * @sideEffect none — pure.
 */
export function resolveAdjustedCloseMs(opts: {
  closeMs: number;
  serverNowMs: number;
  signedMs: number;
}): number {
  return resolveAdjustBaselineMs(opts.closeMs, opts.serverNowMs) + opts.signedMs;
}
